/**
 * TASK-COMBAT-VIZ-006: Unit Lifecycle Tracker Implementation
 *
 * Tracks unit spawning and despawning between combat states.
 * Provides callbacks for rendering unit additions/removals.
 */

import type { CombatState, CombatCreature } from '@drawn-of-war/shared/types/combat';

/**
 * Unit spawn event
 */
export interface UnitSpawnEvent {
  unitId: string;
  unit: CombatCreature;
  tick: number;
}

/**
 * Unit despawn event
 */
export interface UnitDespawnEvent {
  unitId: string;
  unit: CombatCreature;
  tick: number;
}

/**
 * Unit death event (status change from alive to dead)
 * TASK-DEATH-001: Separate from despawn - unit may still be in state.units
 */
export interface UnitDeathEvent {
  unitId: string;
  unit: CombatCreature;
  tick: number;
}

/**
 * Event callback types
 */
type SpawnCallback = (event: UnitSpawnEvent) => void;
type DespawnCallback = (event: UnitDespawnEvent) => void;
type DeathCallback = (event: UnitDeathEvent) => void;

/**
 * UnitLifecycleTracker
 *
 * Tracks unit spawning and despawning between combat state updates.
 */
export class UnitLifecycleTracker {
  private previousUnitIds: Set<string> = new Set();
  private currentUnits: Map<string, CombatCreature> = new Map();
  private spawnedUnits: UnitSpawnEvent[] = [];
  private despawnedUnits: UnitDespawnEvent[] = [];
  private diedUnits: UnitDeathEvent[] = [];
  private isFirstUpdate: boolean = true;

  // TASK-DEATH-001: Track previous status for death detection
  private previousUnitStatus: Map<string, 'alive' | 'dead'> = new Map();

  // Callbacks
  private spawnCallbacks: SpawnCallback[] = [];
  private despawnCallbacks: DespawnCallback[] = [];
  private deathCallbacks: DeathCallback[] = [];

  /**
   * Update with new combat state
   */
  public updateState(state: CombatState): void {
    // Clear previous spawn/despawn/death lists
    this.spawnedUnits = [];
    this.despawnedUnits = [];
    this.diedUnits = [];

    // Build current unit map
    const currentUnitIds = new Set<string>();
    const currentUnitMap = new Map<string, CombatCreature>();
    const currentUnitStatus = new Map<string, 'alive' | 'dead'>();

    state.units.forEach(unit => {
      currentUnitIds.add(unit.unitId);
      currentUnitMap.set(unit.unitId, unit);
      currentUnitStatus.set(unit.unitId, unit.status);
    });

    // Skip spawn/despawn/death detection on first update
    if (!this.isFirstUpdate) {
      // Detect spawned units (in current but not in previous)
      state.units.forEach(unit => {
        if (!this.previousUnitIds.has(unit.unitId)) {
          const spawnEvent: UnitSpawnEvent = {
            unitId: unit.unitId,
            unit,
            tick: state.tick
          };
          this.spawnedUnits.push(spawnEvent);
          this.triggerSpawnCallbacks(spawnEvent);
        }
      });

      // Detect despawned units (in previous but not in current)
      this.previousUnitIds.forEach(unitId => {
        if (!currentUnitIds.has(unitId)) {
          const unit = this.currentUnits.get(unitId)!;
          const despawnEvent: UnitDespawnEvent = {
            unitId,
            unit,
            tick: state.tick
          };
          this.despawnedUnits.push(despawnEvent);
          this.triggerDespawnCallbacks(despawnEvent);
        }
      });

      // TASK-DEATH-001: Detect units that died (status changed from alive to dead)
      // This is separate from despawn - unit may still be in state.units
      state.units.forEach(unit => {
        const previousStatus = this.previousUnitStatus.get(unit.unitId);

        // Only trigger death if:
        // 1. Unit existed before (has previous status)
        // 2. Previous status was 'alive'
        // 3. Current status is 'dead'
        if (previousStatus === 'alive' && unit.status === 'dead') {
          const deathEvent: UnitDeathEvent = {
            unitId: unit.unitId,
            unit,
            tick: state.tick
          };
          this.diedUnits.push(deathEvent);
          this.triggerDeathCallbacks(deathEvent);
        }
      });
    }

    // Update state for next comparison
    this.previousUnitIds = currentUnitIds;
    this.currentUnits = currentUnitMap;
    this.previousUnitStatus = currentUnitStatus;
    this.isFirstUpdate = false;
  }

  /**
   * Get units that spawned in last update
   */
  public getSpawnedUnits(): UnitSpawnEvent[] {
    return [...this.spawnedUnits];
  }

  /**
   * Get units that despawned in last update
   */
  public getDespawnedUnits(): UnitDespawnEvent[] {
    return [...this.despawnedUnits];
  }

  /**
   * Get currently active units
   */
  public getActiveUnits(): CombatCreature[] {
    return Array.from(this.currentUnits.values());
  }

  /**
   * Register callback for unit spawn events
   */
  public onUnitSpawned(callback: SpawnCallback): void {
    this.spawnCallbacks.push(callback);
  }

  /**
   * Register callback for unit despawn events
   */
  public onUnitDespawned(callback: DespawnCallback): void {
    this.despawnCallbacks.push(callback);
  }

  /**
   * TASK-DEATH-001: Register callback for unit death events
   * Death is separate from despawn - triggers when status changes from alive to dead
   */
  public onUnitDeath(callback: DeathCallback): void {
    this.deathCallbacks.push(callback);
  }

  /**
   * TASK-DEATH-001: Get units that died in last update
   */
  public getDiedUnits(): UnitDeathEvent[] {
    return [...this.diedUnits];
  }

  /**
   * Clear all tracked state
   */
  public clear(): void {
    this.previousUnitIds.clear();
    this.currentUnits.clear();
    this.previousUnitStatus.clear();
    this.spawnedUnits = [];
    this.despawnedUnits = [];
    this.diedUnits = [];
    this.isFirstUpdate = true;
  }

  /**
   * Trigger spawn callbacks
   */
  private triggerSpawnCallbacks(event: UnitSpawnEvent): void {
    this.spawnCallbacks.forEach(callback => callback(event));
  }

  /**
   * Trigger despawn callbacks
   */
  private triggerDespawnCallbacks(event: UnitDespawnEvent): void {
    this.despawnCallbacks.forEach(callback => callback(event));
  }

  /**
   * TASK-DEATH-001: Trigger death callbacks
   */
  private triggerDeathCallbacks(event: UnitDeathEvent): void {
    this.deathCallbacks.forEach(callback => callback(event));
  }
}
